/**
 * Utilitarios de limpeza de sessao para o Diagnostico de Jornada.
 *
 * Principio local-first: nenhum dado e enviado para servidores.
 * Este modulo garante que todos os dados em memoria e qualquer
 * residuo defensivo em storage sejam limpos ao fim da sessao.
 */
import type { AppStore } from '../../app/store';

/** Prefixo defensivo para keys de storage (MVP nao usa storage, mas limpa por precaucao). */
const STORAGE_PREFIX = 'diagnostico-';

/**
 * Termina o worker (se ativo) e reseta o store para o estado inicial.
 * Mantém privacyAccepted=true para evitar fricção desnecessária ao re-analisar.
 *
 * @param store - Instancia do AppStore a ser resetada.
 * @param terminateWorker - Callback opcional para encerrar o Web Worker ativo.
 */
export function clearDiagnosticSession(
  store: AppStore,
  terminateWorker?: () => void,
): void {
  if (terminateWorker) {
    terminateWorker();
  }
  resetAppStore(store);
  clearLocalStorageDefensive();
}

/**
 * Reseta o store para o estado inicial, preservando privacyAccepted=true (decisao UX T30).
 * Equivalente a store.clearSession() — exposto como funcao pura para testabilidade.
 *
 * @param store - Instancia do AppStore a ser resetada.
 */
export function resetAppStore(store: AppStore): void {
  store.clearSession();
}

/**
 * Remove defensivamente qualquer chave com prefixo `diagnostico-` do localStorage
 * e do sessionStorage. O MVP nao persiste dados, mas este cleanup protege contra
 * residuos de versoes futuras ou de extensoes de browser.
 *
 * Nao lanca excecao mesmo que storage esteja indisponivel (ex: modo privado restrito).
 */
export function clearLocalStorageDefensive(): void {
  _clearStorageByPrefix(localStorage);
  _clearStorageByPrefix(sessionStorage);
}

function _clearStorageByPrefix(storage: Storage): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => storage.removeItem(key));
  } catch {
    // Storage indisponivel (modo privado restrito ou quota excedida): ignorar silenciosamente.
  }
}
